import { useCallback, useEffect, useState } from "react";
import type { AgentEvent, TaskStatus, WebSocketEvent } from "../lib/types";
import { useWebSocket } from "./useWebSocket";

interface TaskStreamState {
  log: string;
  status: TaskStatus | null;
  tokensUsed: number;
  costUsd: string;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  idleSeconds: number | null;
  loop: { pattern: string; occurrences: number } | null;
  checkpoint: { blocker: string; options?: string[]; completed: string[] } | null;
  events: AgentEvent[];
  lastEventOrdinal: number;
  /** Server-side timestamp of the most recent status flip, used by the
   *  Activity tab to anchor / highlight the latest checkpoint event. */
  statusChangedAt: number;
  /** Counter that increments whenever new spawn proposals arrive — lets
   *  consumers refetch the task without storing the payload twice. */
  spawnProposalsBumped: number;
  connected: boolean;
}

const initial: TaskStreamState = {
  log: "",
  status: null,
  tokensUsed: 0,
  costUsd: "0",
  cacheReadTokens: 0,
  cacheCreationTokens: 0,
  idleSeconds: null,
  loop: null,
  checkpoint: null,
  events: [],
  lastEventOrdinal: -1,
  statusChangedAt: 0,
  spawnProposalsBumped: 0,
  connected: false,
};

export function useTaskStream(taskId: string | null): TaskStreamState {
  const [state, setState] = useState<TaskStreamState>(initial);

  const handle = useCallback((event: unknown) => {
    const evt = event as WebSocketEvent & { type: string };
    if (!evt || typeof evt !== "object" || !("type" in evt)) return;
    setState((prev) => {
      const next = { ...prev };
      switch (evt.type) {
        case "task_output":
          next.log = prev.log + ((evt as { chunk?: string }).chunk ?? "");
          next.idleSeconds = null;
          break;
        case "task_status": {
          const newStatus =
            (evt as { status?: TaskStatus }).status ?? prev.status;
          if (newStatus !== prev.status) next.statusChangedAt = Date.now();
          next.status = newStatus;
          if (next.status !== "in_progress") next.idleSeconds = null;
          break;
        }
        case "task_cost":
          next.tokensUsed =
            (evt as { tokens_used?: number }).tokens_used ?? prev.tokensUsed;
          next.costUsd =
            (evt as { cost_usd?: string }).cost_usd ?? prev.costUsd;
          next.cacheReadTokens =
            (evt as { cache_read_tokens?: number }).cache_read_tokens ??
            prev.cacheReadTokens;
          next.cacheCreationTokens =
            (evt as { cache_creation_tokens?: number }).cache_creation_tokens ??
            prev.cacheCreationTokens;
          break;
        case "task_idle":
          next.idleSeconds =
            (evt as { idle_seconds?: number }).idle_seconds ?? prev.idleSeconds;
          break;
        case "task_loop":
          next.loop = {
            pattern: (evt as { pattern?: string }).pattern ?? "",
            occurrences: (evt as { occurrences?: number }).occurrences ?? 0,
          };
          break;
        case "task_checkpoint": {
          const data = (
            evt as { data?: { blocker?: string; options?: string[]; completed?: string[] } }
          ).data;
          if (data) {
            next.checkpoint = {
              blocker: data.blocker ?? "",
              options: data.options ?? [],
              completed: data.completed ?? [],
            };
          }
          break;
        }
        case "agent_event": {
          const e = evt as {
            ordinal?: number;
            kind?: string;
            payload?: Record<string, unknown>;
            ts?: string;
          };
          if (typeof e.ordinal !== "number") break;
          if (e.ordinal <= prev.lastEventOrdinal) break;
          const agent: AgentEvent = {
            ordinal: e.ordinal,
            kind: (e.kind ?? "raw") as AgentEvent["kind"],
            payload: e.payload ?? {},
            created_at: e.ts,
          };
          next.events = [...prev.events, agent];
          next.lastEventOrdinal = e.ordinal;
          break;
        }
        case "task_spawn_proposals":
          next.spawnProposalsBumped = prev.spawnProposalsBumped + 1;
          break;
      }
      return next;
    });
  }, []);

  // Reset when task changes
  useEffect(() => {
    setState({ ...initial });
  }, [taskId]);

  const { connected } = useWebSocket({
    url: taskId ? `/ws/task/${taskId}` : null,
    onMessage: handle,
  });

  useEffect(() => {
    setState((s) => ({ ...s, connected }));
  }, [connected]);

  return state;
}

/**
 * Merge a chronologically-ordered historical event list with the live
 * events accumulated in the WS stream, preferring whichever copy carries
 * a higher ordinal (in practice they are disjoint — historical stops at
 * the snapshot time and live picks up from there). Stable-sorted by
 * ordinal so the renderer can naively map over the result.
 */
export function mergeEvents(
  historical: AgentEvent[],
  live: AgentEvent[],
): AgentEvent[] {
  const byOrdinal = new Map<number, AgentEvent>();
  for (const e of historical) byOrdinal.set(e.ordinal, e);
  for (const e of live) byOrdinal.set(e.ordinal, e);
  return [...byOrdinal.values()].sort((a, b) => a.ordinal - b.ordinal);
}
